import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export interface MesaElement {
  id: string;
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  acervoItemId?: string;
}

export interface MesaCanvas {
  width: number;
  height: number;
}

export interface Mesa {
  id: string;
  name: string;
  elements: MesaElement[];
  canvas?: MesaCanvas;
  thumbUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SaveMesaResult {
  id: string;
  elements: MesaElement[];
}

const mesaDoc = (userId: string, mesaId: string) => doc(db, 'users', userId, 'mesas', mesaId);
const mesaFolder = (userId: string, mesaId: string) => ref(storage, `mesas/${userId}/${mesaId}`);

const mapMesa = (id: string, data: any): Mesa => ({
  id,
  name: data.name || 'Mesa sem nome',
  elements: Array.isArray(data.elements) ? data.elements : [],
  canvas: data.canvas && typeof data.canvas.width === 'number' ? data.canvas : undefined,
  thumbUrl: data.thumbUrl || undefined,
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
});

export const getUserMesas = async (userId: string): Promise<Mesa[]> => {
  const q = query(collection(db, 'users', userId, 'mesas'), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapMesa(d.id, d.data()));
};

export const getMesa = async (userId: string, mesaId: string): Promise<Mesa | null> => {
  const snap = await getDoc(mesaDoc(userId, mesaId));
  if (!snap.exists()) return null;
  return mapMesa(snap.id, snap.data());
};

const toDataUrl = async (src: string): Promise<string> => {
  if (!src.startsWith('http')) return src;
  const res = await fetch(src);
  if (!res.ok) throw new Error('Uma das imagens da mesa não está mais disponível');
  const blob = await res.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const removeOrphans = async (userId: string, mesaId: string, keepElementIds: string[], keepPreview = true) => {
  try {
    const listing = await listAll(mesaFolder(userId, mesaId));
    const keep = new Set(keepElementIds.map((id) => `${id}.png`));

    await Promise.all(
      listing.items
        .filter((item) => !keep.has(item.name) && item.name !== 'thumb.png' && !(keepPreview && item.name.startsWith('preview_')))
        .map((item) => deleteObject(item).catch(() => undefined))
    );
  } catch {
    return;
  }
};

export const saveMesa = async (
  userId: string,
  mesaId: string | null,
  name: string,
  elements: MesaElement[],
  canvas: MesaCanvas,
  thumbDataUrl?: string
): Promise<SaveMesaResult> => {
  const id = mesaId || doc(collection(db, 'users', userId, 'mesas')).id;
  const folderMarker = `/mesas%2F${userId}%2F${id}%2F`;
  const persistedElements: MesaElement[] = [];
  const uploadedPaths: string[] = [];

  const otherMesaFolder = /\/mesas%2F[^%]+%2F/;

  try {
    for (const el of elements) {
      if (el.src.startsWith('http')) {
        const isFromAnotherMesa = otherMesaFolder.test(el.src) && !el.src.includes(folderMarker);
        if (!isFromAnotherMesa) {
          persistedElements.push(el);
          continue;
        }
      }
      const dataUrl = await toDataUrl(el.src);
      const path = `mesas/${userId}/${id}/${el.id}.png`;
      const objectRef = ref(storage, path);
      await uploadString(objectRef, dataUrl, 'data_url');
      uploadedPaths.push(path);
      persistedElements.push({ ...el, src: await getDownloadURL(objectRef) });
    }

    let thumbUrl: string | undefined;
    if (thumbDataUrl) {
      try {
        const thumbRef = ref(storage, `mesas/${userId}/${id}/thumb.png`);
        await uploadString(thumbRef, thumbDataUrl, 'data_url');
        thumbUrl = await getDownloadURL(thumbRef);
      } catch {
        thumbUrl = undefined;
      }
    }

    const existing = mesaId ? await getDoc(mesaDoc(userId, id)) : null;
    await setDoc(mesaDoc(userId, id), {
      name: name.trim() || 'Mesa sem nome',
      elements: persistedElements,
      canvas,
      ...(thumbUrl ? { thumbUrl } : {}),
      createdAt: existing?.exists() ? existing.data().createdAt : Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    await removeOrphans(userId, id, persistedElements.map((el) => el.id));

    return { id, elements: persistedElements };
  } catch (error) {
    if (!mesaId) {
      await Promise.all(
        uploadedPaths.map((path) => deleteObject(ref(storage, path)).catch(() => undefined))
      );
    }
    throw error;
  }
};

export const deleteMesa = async (userId: string, mesaId: string): Promise<void> => {
  await removeOrphans(userId, mesaId, []);
  await deleteDoc(mesaDoc(userId, mesaId));
};

export const saveMesaPreview = async (
  userId: string,
  mesaId: string,
  eventId: string,
  dataUrl: string
): Promise<string> => {
  const objectRef = ref(storage, `mesas/${userId}/${mesaId}/preview_${eventId}.png`);
  await uploadString(objectRef, dataUrl, 'data_url');
  return getDownloadURL(objectRef);
};
